import React, { useState } from 'react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { supabase } from '../../lib/supabase';
import ProfileTaskGenerationService from '../../services/ProfileTaskGenerationService';
import { RefreshCw, CheckCircle, AlertCircle, Users } from 'lucide-react';

/**
 * ProfileTaskSyncAdmin Component
 *
 * Allows admins to manually trigger profile task generation and sync
 * Useful for testing, recovery, and initialization
 */
const ProfileTaskSyncAdmin = () => {
  const { familyUsers } = useFamilyContext();
  const [syncing, setSyncing] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const children = familyUsers?.filter(user => user.type !== 'parent') || [];

  const handleSync = async (userId) => {
    try {
      setSyncing(true);
      setError(null);
      setResults(null);

      console.log(`[Profile Sync Admin] Starting sync for user ${userId}`);

      const result = await ProfileTaskGenerationService.syncProfileTasksWithEpic(
        supabase,
        userId
      );

      console.log(`[Profile Sync Admin] Sync complete:`, result);

      setResults({
        userId,
        ...result,
        timestamp: new Date().toISOString()
      });

      setSyncing(false);
    } catch (err) {
      console.error('[Profile Sync Admin] Sync failed:', err);
      setError(err.message || 'Failed to sync profile tasks');
      setSyncing(false);
    }
  };

  const handleSyncAll = async () => {
    try {
      setSyncing(true);
      setError(null);
      setResults(null);

      const allResults = [];

      for (const child of children) {
        try {
          const result = await ProfileTaskGenerationService.syncProfileTasksWithEpic(
            supabase,
            child.id
          );
          allResults.push({
            userId: child.id,
            userName: child.name || child.displayName,
            ...result
          });
        } catch (err) {
          console.error(`Failed to sync user ${child.id}:`, err);
          allResults.push({
            userId: child.id,
            userName: child.name || child.displayName,
            error: err.message
          });
        }
      }

      setResults({
        bulkSync: true,
        results: allResults,
        timestamp: new Date().toISOString()
      });

      setSyncing(false);
    } catch (err) {
      console.error('[Profile Sync Admin] Bulk sync failed:', err);
      setError(err.message || 'Failed to sync all users');
      setSyncing(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          Profile Task Sync
        </h3>
        <p className="text-sm text-slate-600">
          Manually trigger profile task generation and synchronization. This will:
        </p>
        <ul className="mt-2 text-sm text-slate-600 list-disc list-inside space-y-1">
          <li>Generate tasks for incomplete profile sections</li>
          <li>Update existing tasks with current completion percentages</li>
          <li>Auto-complete tasks for 100% complete sections</li>
          <li>Remove orphaned profile tasks</li>
        </ul>
      </div>

      {/* User Selection */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-slate-700 mb-3">
          Select Student
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {children.map(child => (
            <button
              key={child.id}
              onClick={() => setSelectedUserId(child.id)}
              disabled={syncing}
              className={`px-4 py-3 rounded-lg border-2 transition-all ${
                selectedUserId === child.id
                  ? 'border-violet-500 bg-violet-50 text-violet-900 font-semibold'
                  : 'border-slate-200 bg-white text-slate-700 hover:border-violet-300 hover:bg-violet-50'
              } ${syncing ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {child.name || child.displayName}
            </button>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 mb-6">
        <button
          onClick={() => selectedUserId && handleSync(selectedUserId)}
          disabled={!selectedUserId || syncing}
          className="flex-1 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
        >
          {syncing ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" />
              <span>Syncing...</span>
            </>
          ) : (
            <>
              <RefreshCw className="w-4 h-4" />
              <span>Sync Selected User</span>
            </>
          )}
        </button>

        <button
          onClick={handleSyncAll}
          disabled={children.length === 0 || syncing}
          className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
        >
          {syncing ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" />
              <span>Syncing All...</span>
            </>
          ) : (
            <>
              <Users className="w-4 h-4" />
              <span>Sync All Users</span>
            </>
          )}
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2 text-red-700">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span className="font-medium">Sync Failed</span>
          </div>
          <p className="mt-2 text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Results Display */}
      {results && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
          <div className="flex items-center gap-2 text-emerald-700 mb-3">
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
            <span className="font-semibold">Sync Complete</span>
          </div>

          {results.bulkSync ? (
            // Bulk sync results
            <div className="space-y-3">
              <p className="text-sm text-emerald-700">
                Synced {results.results.length} user{results.results.length !== 1 ? 's' : ''}
              </p>
              <div className="space-y-2">
                {results.results.map((userResult, index) => (
                  <div key={index} className="bg-white rounded p-3 text-sm">
                    <div className="font-medium text-slate-900 mb-1">
                      {userResult.userName}
                    </div>
                    {userResult.error ? (
                      <div className="text-red-600">{userResult.error}</div>
                    ) : (
                      <div className="text-slate-600 space-y-1">
                        <div>✓ {userResult.inserted || 0} tasks created</div>
                        <div>✓ {userResult.updated || 0} tasks updated</div>
                        <div>✓ {userResult.completed || 0} tasks completed</div>
                        <div className="font-medium text-emerald-700">
                          Total: {userResult.total || 0} profile tasks
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            // Single user sync results
            <div className="space-y-2 text-sm text-emerald-700">
              <div className="flex justify-between">
                <span>Tasks Created:</span>
                <span className="font-semibold">{results.inserted || 0}</span>
              </div>
              <div className="flex justify-between">
                <span>Tasks Updated:</span>
                <span className="font-semibold">{results.updated || 0}</span>
              </div>
              <div className="flex justify-between">
                <span>Tasks Completed:</span>
                <span className="font-semibold">{results.completed || 0}</span>
              </div>
              <div className="pt-2 border-t border-emerald-200">
                <div className="flex justify-between font-semibold">
                  <span>Total Profile Tasks:</span>
                  <span>{results.total || 0}</span>
                </div>
              </div>
            </div>
          )}

          <div className="mt-3 pt-3 border-t border-emerald-200 text-xs text-emerald-600">
            Synced at {new Date(results.timestamp).toLocaleTimeString()}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileTaskSyncAdmin;
